import { Modal, Form, Input, InputNumber, Select, Switch, Tag, App } from 'antd'
import { useState, useEffect } from 'react'
import { createQuestion, updateQuestion } from '../services/survey-question-api'
import type { SurveyQuestion } from '../types/survey.types'
interface CustomTagProps {
  label: React.ReactNode
  value: any
  disabled: boolean
  onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void
  closable: boolean
  isMaxTag: boolean
}

interface QuestionFormModalProps {
  open: boolean
  surveyId: string
  question: SurveyQuestion | null
  nextStep: number
  onClose: () => void
  onSuccess: () => void
}

interface FormValues {
  step: number
  label: string
  type: string
  options: string[]
  hasOther: boolean
  isOptional: boolean
}

const questionTypes = [
  { label: 'Multi-select', value: 'multi-select' },
  { label: 'Single-select', value: 'single-select' },
  { label: 'Text', value: 'text' }
]

const tagColors = ['blue', 'green', 'orange', 'purple', 'cyan', 'magenta', 'geekblue', 'volcano', 'gold', 'lime']

export function QuestionFormModal({ open, surveyId, question, nextStep, onClose, onSuccess }: QuestionFormModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [loading, setLoading] = useState(false)
  const type = Form.useWatch('type', form)
  const isEdit = !!question

  useEffect(() => {
    if (open && question) {
      let options: string[] = []
      try {
        if (question.options) {
          options = JSON.parse(question.options)
        }
      } catch {
        options = []
      }

      form.setFieldsValue({
        step: question.step,
        label: question.label,
        type: question.type,
        options,
        hasOther: question.hasOther,
        isOptional: question.isOptional
      })
    } else if (open) {
      form.resetFields()
      form.setFieldValue('step', nextStep)
    }
  }, [open, question, form, nextStep])

  const showOptions = type === 'multi-select' || type === 'single-select'

  const handleTypeChange = (newType: string) => {
    if (newType === 'text') {
      form.setFieldsValue({ options: [], hasOther: false })
    }
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true)
      const optionsJson = showOptions && values.options?.length > 0 ? JSON.stringify(values.options) : null

      if (isEdit) {
        await updateQuestion(surveyId, question.id, {
          id: question.id,
          surveyId,
          step: values.step,
          label: values.label,
          type: values.type,
          options: optionsJson,
          hasOther: showOptions ? values.hasOther : false,
          isOptional: values.isOptional
        })
        message.success('Question updated')
      } else {
        await createQuestion(surveyId, {
          step: values.step,
          label: values.label,
          type: values.type,
          options: optionsJson,
          hasOther: showOptions ? values.hasOther : false,
          isOptional: values.isOptional
        })
        message.success('Question created')
      }

      form.resetFields()
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving question:', error)
      message.error(error.response?.data?.message || 'Failed to save question')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const tagRender = (props: CustomTagProps) => {
    const { label, value, closable, onClose: onTagClose } = props
    const index = (form.getFieldValue('options') as string[])?.indexOf(value as string) ?? 0
    const color = tagColors[index % tagColors.length]

    return (
      <Tag
        color={color}
        closable={closable}
        onClose={onTagClose}
        style={{ marginInlineEnd: 4, padding: '2px 8px', fontSize: 13 }}
      >
        {label}
      </Tag>
    )
  }

  return (
    <Modal
      title={isEdit ? 'Edit question' : 'Add question'}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isEdit ? 'Save' : 'Add'}
      confirmLoading={loading}
      width={560}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          step: 1,
          type: 'multi-select',
          hasOther: false,
          isOptional: false,
          options: []
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '16px' }}>
          <Form.Item label="Step" name="step" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Type" name="type" rules={[{ required: true, message: 'Required' }]}>
            <Select options={questionTypes} onChange={handleTypeChange} />
          </Form.Item>
        </div>

        <Form.Item
          label="Question"
          name="label"
          rules={[
            { required: true, message: 'Please enter the question text' },
            { max: 500, message: 'Question must not exceed 500 characters' }
          ]}
        >
          <Input.TextArea rows={2} placeholder="Enter the question text..." />
        </Form.Item>

        {showOptions && (
          <Form.Item
            label="Options"
            name="options"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error('Please add at least one option'))
                  }
                  return Promise.resolve()
                }
              }
            ]}
            extra="Type an option and press Enter to add"
          >
            <Select
              mode="tags"
              placeholder="e.g. Strongly Agree"
              open={false}
              tagRender={tagRender}
              suffixIcon={null}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <div style={{ display: 'flex', gap: '24px' }}>
          {showOptions && (
            <Form.Item label="Has 'Others' option" name="hasOther" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Form.Item label="Optional" name="isOptional" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
